/**
 * Component for adding new rules to the reading list
 */

import './AddRuleForm.css';
import { useState } from 'react';
import { RULE_TYPES, OPERATORS, generateRuleId, LITERATURE_TYPES } from '../../utils/ruleValidation';
import { BOOK_TYPES } from '../../constants/bookConstants';

function AddRuleForm({ onAdd, onCancel }) {
    const [ruleType, setRuleType] = useState('');
    const [operator, setOperator] = useState(OPERATORS.MIN);
    const [formData, setFormData] = useState({
        value: 2,
        bookType: 'PROSE',
        literatureType: LITERATURE_TYPES.CZECH,
        yearFrom: '',
        yearTo: ''
    });

    const handleRuleTypeChange = (e) => {
        setRuleType(e.target.value);
    };

    const handleOperatorChange = (e) => {
        setOperator(e.target.value);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        let processedValue = value;

        if (name === 'value') {
            processedValue = parseInt(value, 10);
        } else if (name === 'yearFrom' || name === 'yearTo') {
            // Keep as string if empty, otherwise parse as number
            processedValue = value === '' ? '' : parseInt(value, 10);
        }

        setFormData({
            ...formData,
            [name]: processedValue
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!ruleType) {
            alert('Vyberte typ pravidla');
            return;
        }

        if (formData.value < 1) {
            alert('Počet musí být alespoň 1');
            return;
        }

        let newRule = {
            id: generateRuleId(),
            type: ruleType,
            operator: operator,
            value: formData.value,
            createdAt: Date.now()
        };

        switch (ruleType) {
            case RULE_TYPES.BOOKS_PER_AUTHOR:
                // No additional fields needed
                break;

            case RULE_TYPES.BOOKS_BY_TYPE:
                newRule.bookType = formData.bookType;
                break;

            case RULE_TYPES.BOOKS_BY_COUNTRY:
                newRule.literatureType = formData.literatureType;
                break;

            case RULE_TYPES.BOOKS_BY_YEAR_RANGE:
                // At least one bound must be specified
                if (formData.yearFrom === '' && formData.yearTo === '') {
                    alert('Zadejte alespoň jeden rok (od nebo do)');
                    return;
                }
                // Validate year values if provided
                if (formData.yearFrom !== '' && (formData.yearFrom < 1 || formData.yearFrom > 9999)) {
                    alert('Rok "Od" musí být mezi 1 a 9999');
                    return;
                }
                if (formData.yearTo !== '' && (formData.yearTo < 1 || formData.yearTo > 9999)) {
                    alert('Rok "Do" musí být mezi 1 a 9999');
                    return;
                }
                // Validate range
                if (formData.yearFrom !== '' && formData.yearTo !== '' && formData.yearFrom > formData.yearTo) {
                    alert('Rok "Od" musí být menší nebo roven roku "Do"');
                    return;
                }
                newRule.yearFrom = formData.yearFrom === '' ? null : formData.yearFrom;
                newRule.yearTo = formData.yearTo === '' ? null : formData.yearTo;
                break;

            default:
                alert('Neplatný typ pravidla');
                return;
        }

        onAdd(newRule);

        // Reset form
        setRuleType('');
        setOperator(OPERATORS.MIN);
        setFormData({
            value: 2,
            bookType: 'PROSE',
            literatureType: LITERATURE_TYPES.CZECH,
            yearFrom: '',
            yearTo: ''
        });
    };

    return (
        <form className="add-rule-form" onSubmit={handleSubmit}>
            <div className="form-group">
                <label htmlFor="ruleType">Typ pravidla</label>
                <select
                    id="ruleType"
                    value={ruleType}
                    onChange={handleRuleTypeChange}
                    className="form-select"
                >
                    <option value="">-- Vyberte typ pravidla --</option>
                    <option value={RULE_TYPES.BOOKS_PER_AUTHOR}>
                        Počet knih od jednoho autora
                    </option>
                    <option value={RULE_TYPES.BOOKS_BY_TYPE}>
                        Počet knih určitého literárního druhu
                    </option>
                    <option value={RULE_TYPES.BOOKS_BY_COUNTRY}>
                        Počet knih české nebo světové literatury
                    </option>
                    <option value={RULE_TYPES.BOOKS_BY_YEAR_RANGE}>
                        Počet knih z časového období
                    </option>
                </select>
            </div>

            {ruleType && (
                <>
                    <div className="form-group">
                        <label htmlFor="operator">Typ podmínky</label>
                        <select
                            id="operator"
                            value={operator}
                            onChange={handleOperatorChange}
                            className="form-select"
                        >
                            <option value={OPERATORS.MIN}>Minimálně</option>
                            <option value={OPERATORS.MAX}>Maximálně</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label htmlFor="value">Počet</label>
                        <input
                            type="number"
                            id="value"
                            name="value"
                            value={formData.value}
                            onChange={handleInputChange}
                            min="1"
                            max="20"
                            className="form-input"
                        />
                    </div>
                </>
            )}

            {ruleType === RULE_TYPES.BOOKS_BY_TYPE && (
                <div className="form-group">
                    <label htmlFor="bookType">Literární druh</label>
                    <select
                        id="bookType"
                        name="bookType"
                        value={formData.bookType}
                        onChange={handleInputChange}
                        className="form-select"
                    >
                        {BOOK_TYPES.map(type => (
                            <option key={type.value} value={type.value}>
                                {type.label}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            {ruleType === RULE_TYPES.BOOKS_BY_COUNTRY && (
                <div className="form-group">
                    <label htmlFor="literatureType">Typ literatury</label>
                    <select
                        id="literatureType"
                        name="literatureType"
                        value={formData.literatureType}
                        onChange={handleInputChange}
                        className="form-select"
                    >
                        <option value={LITERATURE_TYPES.CZECH}>Česká literatura</option>
                        <option value={LITERATURE_TYPES.WORLD}>Světová literatura</option>
                    </select>
                </div>
            )}

            {ruleType === RULE_TYPES.BOOKS_BY_YEAR_RANGE && (
                <>
                    <div className="form-group">
                        <label htmlFor="yearFrom">Od roku (nepovinné)</label>
                        <input
                            type="number"
                            id="yearFrom"
                            name="yearFrom"
                            value={formData.yearFrom}
                            onChange={handleInputChange}
                            min="1"
                            max="9999"
                            placeholder="např. 1850"
                            className="form-input"
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="yearTo">Do roku (nepovinné)</label>
                        <input
                            type="number"
                            id="yearTo"
                            name="yearTo"
                            value={formData.yearTo}
                            onChange={handleInputChange}
                            min="1"
                            max="9999"
                            placeholder="např. 1950"
                            className="form-input"
                        />
                    </div>
                </>
            )}

            <div className="form-actions">
                <button type="submit" className="btn-add-rule" disabled={!ruleType}>
                    Přidat pravidlo
                </button>
                <button type="button" className="btn-cancel" onClick={onCancel}>
                    Zrušit
                </button>
            </div>
        </form>
    );
}

export default AddRuleForm;
